// Lookbook modal. Connects all lookbook items into one navigable sequence
// (media + details + thumbnail filmstrip synced by index). Opens on grid-item
// click with a GSAP Flip fly-in (falls back to a fade if gsap/Flip are absent).
if (!customElements.get('lookbook-modal')) {
  const hasFlip = () => typeof window.gsap !== 'undefined' && typeof window.Flip !== 'undefined';

  class LookbookModal extends HTMLElement {
    connectedCallback() {
      this.dialog = this.querySelector('[data-lookbook-dialog]');
      this.mediaTrack = this.querySelector('[data-media-track]');
      this.mediaViewport = this.querySelector('[data-media-viewport]');
      this.detailsTrack = this.querySelector('[data-details-track]');
      this.mediaSlides = Array.from(this.querySelectorAll('[data-media-slide]'));
      this.detailsSlides = Array.from(this.querySelectorAll('[data-details-slide]'));
      this.thumbs = Array.from(this.querySelectorAll('[data-thumb]'));
      this.filmstrip = this.querySelector('[data-filmstrip]');
      this.currentEl = this.querySelector('[data-current]');
      this.count = this.mediaSlides.length;
      this.index = 0;

      this._onKeydown = this.onKeydown.bind(this);

      this.querySelectorAll('[data-prev]').forEach((el) => el.addEventListener('click', () => this.step(-1)));
      this.querySelectorAll('[data-next]').forEach((el) => el.addEventListener('click', () => this.step(1)));
      this.querySelector('[data-close]')?.addEventListener('click', () => this.close());
      this.querySelector('[data-overlay]')?.addEventListener('click', () => this.close());
      this.querySelector('[data-share]')?.addEventListener('click', () => this.share());
      this.thumbs.forEach((thumb) => {
        thumb.addEventListener('click', () => this.goTo(parseInt(thumb.dataset.index, 10)));
      });

      // Native scroll-snap on the media provides swipe; sync the index from it.
      let scrollTimer;
      this.mediaViewport?.addEventListener('scroll', () => {
        clearTimeout(scrollTimer);
        scrollTimer = setTimeout(() => this.syncFromScroll(), 90);
      }, { passive: true });

      this.openFromUrl();
    }

    // Update index from the media's scroll position (after a swipe), then sync chrome.
    syncFromScroll() {
      if (!this.mediaViewport || !this.mediaViewport.clientWidth) return;
      const i = Math.round(this.mediaViewport.scrollLeft / this.mediaViewport.clientWidth);
      if (i !== this.index && i >= 0 && i < this.count) {
        this.index = i;
        this.updateChrome();
      }
    }

    // Deep link: /pages/...?i=<image-id> opens the modal at that item.
    openFromUrl() {
      const id = new URLSearchParams(location.search).get('i');
      if (!id) return;
      const slide = this.mediaSlides.find((s) => s.dataset.flipId === String(id));
      if (slide) this.open(id, null, null);
    }

    open(mediaId, openerMedia, openerButton) {
      this.openerMedia = openerMedia || null;
      this.openerButton = openerButton || null;
      this._lastFocus = document.activeElement;

      const idx = this.mediaSlides.findIndex((s) => s.dataset.flipId === String(mediaId));
      this.index = idx >= 0 ? idx : 0;

      this.setAttribute('open', '');
      document.documentElement.classList.add('lookbook-modal-active');
      this.render(false);
      document.addEventListener('keydown', this._onKeydown);

      requestAnimationFrame(() => this.flipIn());
      this.dialog?.focus({ preventScroll: true });
    }

    close() {
      this.removeAttribute('open');
      document.documentElement.classList.remove('lookbook-modal-active');
      document.removeEventListener('keydown', this._onKeydown);
      if (this.openerButton) {
        this.openerButton.focus({ preventScroll: true });
      } else if (this._lastFocus && this._lastFocus.focus) {
        this._lastFocus.focus();
      }
    }

    isOpen() {
      return this.hasAttribute('open');
    }

    step(dir) {
      this.goTo(this.index + dir);
    }

    goTo(index) {
      const clamped = Math.max(0, Math.min(this.count - 1, index));
      if (clamped === this.index) return;
      this.index = clamped;
      this.render(true);
    }

    render(animate) {
      // Media navigates via native scroll-snap (which also enables touch swipe).
      if (this.mediaViewport) {
        this.mediaViewport.scrollTo({
          left: this.index * this.mediaViewport.clientWidth,
          behavior: animate ? 'smooth' : 'auto',
        });
      }
      this.updateChrome(animate);
    }

    // Everything that follows the active index except the media scroll itself.
    updateChrome(animate) {
      if (this.detailsTrack) {
        const offset = `translateX(${-this.index * 100}%)`;
        if (animate === false) {
          this.detailsTrack.style.transition = 'none';
          this.detailsTrack.style.transform = offset;
          void this.detailsTrack.offsetWidth;
          this.detailsTrack.style.transition = '';
        } else {
          this.detailsTrack.style.transform = offset;
        }
      }

      if (this.currentEl) this.currentEl.textContent = this.index + 1;

      this.thumbs.forEach((thumb, i) => {
        thumb.setAttribute('aria-current', i === this.index ? 'true' : 'false');
      });

      this.querySelectorAll('[data-prev]').forEach((el) => { el.disabled = this.index === 0; });
      this.querySelectorAll('[data-next]').forEach((el) => { el.disabled = this.index === this.count - 1; });

      this.scrollThumbIntoView();
    }

    scrollThumbIntoView() {
      const thumb = this.thumbs[this.index];
      if (!thumb || !this.filmstrip) return;
      const thumbRect = thumb.getBoundingClientRect();
      const stripRect = this.filmstrip.getBoundingClientRect();
      if (thumbRect.left < stripRect.left || thumbRect.right > stripRect.right) {
        thumb.scrollIntoView({ behavior: 'smooth', inline: 'center', block: 'nearest' });
      }
    }

    onKeydown(event) {
      if (!this.isOpen()) return;
      switch (event.key) {
        case 'Escape':
          this.close();
          break;
        case 'ArrowLeft':
          this.step(-1);
          break;
        case 'ArrowRight':
          this.step(1);
          break;
      }
    }

    share() {
      const slide = this.detailsSlides[this.index];
      const id = slide?.dataset.shareId || this.mediaSlides[this.index]?.dataset.flipId;
      const title = slide?.dataset.shareTitle || document.title;
      const url = `${location.origin}${location.pathname}?i=${id}`;

      if (navigator.share) {
        navigator.share({ title, url }).catch(() => {});
      } else if (navigator.clipboard) {
        navigator.clipboard.writeText(url).catch(() => {});
      }
    }

    flipIn() {
      const target = this.mediaSlides[this.index]?.querySelector('img') || this.mediaSlides[this.index];
      const source = this.openerMedia?.querySelector('img') || this.openerMedia;

      if (!hasFlip() || !source || !target) return;

      try {
        window.Flip.fit(target, source, { scale: true });
        const state = window.Flip.getState(target);
        window.gsap.set(target, { clearProps: 'transform,width,height' });
        window.Flip.from(state, {
          duration: 0.5,
          ease: 'power2.inOut',
          scale: true,
          absolute: true,
          onComplete: () => window.gsap.set(target, { clearProps: 'transform,width,height' }),
        });
      } catch (error) {
        console.debug('Lookbook modal: Flip animation failed, showing without it.', error);
      }
    }
  }

  customElements.define('lookbook-modal', LookbookModal);

  class LookbookModalOpener extends HTMLElement {
    connectedCallback() {
      this.button = this.querySelector('.lookbook__media-toggle') || this.querySelector('button');
      this.media = this.querySelector('[data-flip-id]');
      this.modal = document.querySelector(this.dataset.modal);

      if (!this.button || !this.modal) return;
      this.button.addEventListener('click', () => {
        this.modal.open(this.dataset.mediaId, this.media, this.button);
      });
    }
  }

  customElements.define('lookbook-modal-opener', LookbookModalOpener);
}
